/**
 * Apple 의 두 파라미터로 말하는 스프링 어휘.
 *
 * Apple 은 mass·stiffness·damping 3개를 디자이너용 2개로 바꿨어요:
 *
 * - **damping ratio** — 넘어감(overshoot). `1.0` 이면 안 튀고 부드럽게 앉고,
 *   `1.0` 아래면 넘어가서 진동해요.
 * - **response** — 목표에 닿는 데 걸리는 시간(초). **duration 이 아닙니다** —
 *   스프링은 고정 길이가 없고 settle 시간은 파라미터에서 따라 나와요.
 *
 * `motion` 의 `visualDuration` 이 정확히 이 response 예요(목표에 **시각적으로**
 * 닿는 시간이고 settle 은 그 뒤에 알아서 끝납니다). `duration` 을 쓰면 settle
 * 까지를 그 시간에 밀어넣어서 같은 숫자가 더 느리게 느껴져요.
 *
 * 왜 스프링인가: 길이가 고정된 애니메이션은 새 입력에 답할 수 없어요. 스프링은
 * 목표만 바꾸면 되고 진행 중인 값에서 이어지니까, 사용자가 움직이는 요소를
 * 잡아서 되돌려도 튀지 않습니다.
 */

/** `motion` 의 `transition` 에 그대로 넣는 모양. */
export interface SpringTransition {
  type: "spring";
  /** `0` = 안 튐(임계 감쇠), `1` = 아주 많이 튐. */
  bounce: number;
  /** Apple 의 response(초). */
  visualDuration: number;
}

/**
 * Apple 의 (damping, response) → `motion` 의 (bounce, visualDuration).
 *
 * `bounce = 1 - damping` 이에요. 두 API 가 같은 축을 반대 방향에서 세는 것뿐입니다.
 * 과감쇠(damping > 1)는 bounce 를 음수로 만드는데 `motion` 이 그걸 받지 않으므로
 * 0 에서 잘라요 — "더 안 튀는 것" 은 안 튀는 것과 같습니다.
 */
export function appleSpring(damping: number, response: number): SpringTransition {
  return {
    type: "spring",
    bounce: Math.min(Math.max(1 - damping, 0), 1),
    visualDuration: response,
  };
}

/**
 * Apple 이 실제로 쓰는 값들(WWDC 2018 *Designing Fluid Interfaces*).
 *
 * 기본은 **임계 감쇠**(`damping 1.0`)예요. 튐은 **제스처가 운동량을 실어 준
 * 뒤에만** 붙입니다 — 그냥 나타난 메뉴가 넘어가면 이유 없이 출렁이고, 손으로
 * 던진 카드가 넘어가면 물리적으로 맞아요.
 */
export const SPRINGS = {
  /** 자리 옮김(PiP 이동 등). 화면 대부분의 기본값. */
  move: appleSpring(1.0, 0.4),
  /** UI 상태 전환 — 위치 이동보다 짧아야 조작에 붙어 있는 느낌이 나요. */
  ui: appleSpring(1.0, 0.3),
  /** 회전 — 운동량이 실리는 축이라 살짝 넘어가요. */
  rotate: appleSpring(0.8, 0.4),
  /** 서랍·시트 — 손으로 끌어 여는 물건이라 튐이 있습니다. */
  sheet: appleSpring(0.8, 0.3),
} as const;

/**
 * `prefers-reduced-motion` 일 때 쓰는 전환.
 *
 * 모션을 **끄지 않아요** — 더 순한 것으로 바꿉니다. 이 설정을 켠 사람이 피하려는
 * 건 화면이 미끄러지는 것(전정기관 자극)이고, 불투명도 교차는 그 자극이 없으면서
 * "새 게 왔다·갔다" 를 그대로 전달해요. 아예 0 으로 만들면 토스트가 순간이동해서
 * 뭐가 바뀌었는지 놓치게 됩니다.
 *
 * 스프링이 아니라 tween 이에요 — 넘어감(overshoot)도 전정기관 자극입니다.
 */
export const CROSSFADE = { duration: 0.2, ease: "easeOut" } as const;

/**
 * 제스처를 놓았을 때 **어디까지 갈지** 를 속도로 투사해요.
 *
 * 놓은 자리에서 가장 가까운 스냅으로 붙이면 세게 튕겨도 제자리로 돌아와서
 * "던졌는데 안 날아간다" 가 됩니다. 스크롤 감속과 같은 식으로 도착점을 먼저
 * 구하고, 그 점 기준으로 스냅을 고르면 작은 입력이 큰 출력이 돼요.
 *
 * 교과서의 `v² / (2·a)` 가 아니라 **지수 감쇠** 형태예요 — Apple 샘플 코드가
 * 쓰는 식이고, 두 식은 결과가 꽤 다릅니다.
 *
 * @param velocity 놓는 순간의 속도(px/s).
 * @param decelerationRate `0.998` 이 일반 스크롤 감각, `0.99` 는 더 빨리 멈춤.
 * @returns 현재 위치에 **더할** 거리(px).
 */
export function project(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/**
 * 경계 밖에서 점점 더 안 따라오게 만들어요(고무줄).
 *
 * 딱 멈추면 "얼었다" 로 읽히고, 계속 저항하면 "반응은 하는데 더 없다" 로
 * 읽혀요. 실제 물건도 멈추기 전에 느려집니다.
 *
 * @param overshoot 경계를 넘어간 거리(px, 부호 유지).
 * @param dimension 그 축의 크기(px) — 큰 면일수록 더 늘어나요.
 */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  if (dimension <= 0) return 0;
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

/** 투사된 도착점에서 가장 가까운 스냅. 후보가 없으면 도착점 그대로예요. */
export function nearestSnap(value: number, snaps: readonly number[]): number {
  if (snaps.length === 0) return value;
  return snaps.reduce((best, snap) =>
    Math.abs(snap - value) < Math.abs(best - value) ? snap : best
  );
}

/**
 * 제스처 속도를 남은 거리로 정규화해요(정규화 속도를 받는 스프링 API 용).
 *
 * 남은 거리가 0 이면 나눗셈이 무한이 되는데, 그건 "이미 목표" 라는 뜻이라
 * 0 을 돌려줍니다 — `Infinity` 를 스프링에 넣으면 값이 `NaN` 으로 죽어요.
 */
export function relativeVelocity(velocity: number, current: number, target: number): number {
  const distance = target - current;
  if (distance === 0) return 0;
  return velocity / distance;
}
